import React, { useState, useEffect } from 'react'
import { Card, Button, Space, Typography, List, Tag, Progress, Alert, Statistic, Row, Col, Collapse, Tooltip, Badge } from 'antd'
import { SafetyCertificateOutlined, CheckCircleOutlined, WarningOutlined, CloseCircleOutlined, InfoCircleOutlined, DollarOutlined, FileTextOutlined } from '@ant-design/icons'
import { useDesignStore } from '../../store/designStore'
import ComplianceChecker, { ComplianceReport, ComplianceResult, DEFAULT_ZONING_RULES, DEFAULT_BUILDING_CODES } from '../../services/compliance/ComplianceChecker'

const { Title, Text } = Typography
const { Panel } = Collapse

interface CompliancePanelProps {
  onClose: () => void
}

const CompliancePanel: React.FC<CompliancePanelProps> = ({ onClose }) => {
  const { getAllObjects } = useDesignStore()
  const [complianceReport, setComplianceReport] = useState<ComplianceReport | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)

  const allObjects = getAllObjects()

  // Initialize compliance checker
  const complianceChecker = new ComplianceChecker(
    DEFAULT_ZONING_RULES,
    DEFAULT_BUILDING_CODES,
    {
      lot_size: 9600, // 80' x 120' = 9,600 sq ft
      existing_coverage: 1200, // Existing house coverage
      existing_impervious: 2000 // Existing impervious surfaces
    }
  )

  // Run compliance check
  const runComplianceCheck = async () => {
    setIsAnalyzing(true)
    
    // Simulate analysis time
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    const report = complianceChecker.checkCompliance(allObjects)
    setComplianceReport(report)
    setIsAnalyzing(false)
  }

  // Auto-run compliance check when objects change
  useEffect(() => {
    if (allObjects.length > 0) {
      runComplianceCheck()
    }
  }, [allObjects.length])

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return '#52c41a'
      case 'warning': return '#faad14'
      case 'violation': return '#ff4d4f'
      case 'requires_permit': return '#1890ff'
      default: return '#d9d9d9'
    }
  }

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant': return <CheckCircleOutlined style={{ color: '#52c41a' }} />
      case 'warning': return <WarningOutlined style={{ color: '#faad14' }} />
      case 'violation': return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
      case 'requires_permit': return <FileTextOutlined style={{ color: '#1890ff' }} />
      default: return <InfoCircleOutlined />
    }
  }

  // Get severity color
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'green'
      case 'medium': return 'orange'
      case 'high': return 'red'
      case 'critical': return 'red'
      default: return 'default'
    }
  }

  // Group results by type
  const groupResultsByType = (results: ComplianceResult[]) => {
    const groups: Record<string, ComplianceResult[]> = {}
    results.forEach(result => {
      if (!groups[result.type]) {
        groups[result.type] = []
      }
      groups[result.type].push(result)
    })
    return groups
  }

  const getOverallStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return '#52c41a'
      case 'needs_attention': return '#faad14'
      case 'major_violations': return '#ff4d4f'
      default: return '#d9d9d9'
    }
  }

  const getOverallStatusText = (status: string) => {
    switch (status) {
      case 'compliant': return 'Fully Compliant'
      case 'needs_attention': return 'Needs Attention'
      case 'major_violations': return 'Major Violations'
      default: return 'Unknown'
    }
  }

  return (
    <Card
      title={
        <Space>
          <SafetyCertificateOutlined />
          <span>Compliance Checker</span>
        </Space>
      }
      extra={
        <Button type="text" onClick={onClose}>
          ×
        </Button>
      }
      style={{
        position: 'fixed',
        top: '80px',
        right: '20px',
        width: '500px',
        maxHeight: '85vh',
        overflowY: 'auto',
        zIndex: 1000,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* Analysis Button */}
        {allObjects.length === 0 && (
          <Alert
            message="No Objects to Analyze"
            description="Add an ADU or Pool to your design to run compliance checks."
            type="info"
            showIcon
          />
        )}

        {allObjects.length > 0 && (
          <Button
            type="primary"
            icon={<SafetyCertificateOutlined />}
            onClick={runComplianceCheck}
            loading={isAnalyzing}
            block
            size="large"
          >
            {isAnalyzing ? 'Analyzing Compliance...' : 'Run Compliance Check'}
          </Button>
        )}

        {/* Analysis Progress */}
        {isAnalyzing && (
          <div>
            <Text type="secondary" style={{ fontSize: '12px', marginBottom: '8px', display: 'block' }}>
              🏛️ Checking zoning and building code compliance...
            </Text>
            <Progress percent={100} showInfo={false} status="active" />
            <Space direction="vertical" style={{ width: '100%', marginTop: '8px' }}>
              <Text style={{ fontSize: '11px', color: '#666' }}>✓ Checking zoning requirements</Text>
              <Text style={{ fontSize: '11px', color: '#666' }}>✓ Validating building codes</Text>
              <Text style={{ fontSize: '11px', color: '#666' }}>✓ Reviewing ADA compliance</Text>
              <Text style={{ fontSize: '11px', color: '#666' }}>✓ Assessing fire safety</Text>
              <Text style={{ fontSize: '11px', color: '#666' }}>✓ Evaluating utility requirements</Text>
            </Space>
          </div>
        )}

        {/* Compliance Report */}
        {complianceReport && (
          <div>
            {/* Overall Status */}
            <Card size="small" style={{ marginBottom: '16px' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Compliance Score"
                    value={complianceReport.compliance_score}
                    suffix="/ 100"
                    valueStyle={{ color: getOverallStatusColor(complianceReport.overall_status) }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Overall Status"
                    value={getOverallStatusText(complianceReport.overall_status)}
                    valueStyle={{ 
                      color: getOverallStatusColor(complianceReport.overall_status),
                      fontSize: '16px'
                    }}
                  />
                </Col>
              </Row>
              
              <Row gutter={16} style={{ marginTop: '16px' }}>
                <Col span={6}>
                  <Statistic
                    title="Passed"
                    value={complianceReport.passed_checks}
                    valueStyle={{ color: '#52c41a', fontSize: '14px' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Warnings"
                    value={complianceReport.warnings}
                    valueStyle={{ color: '#faad14', fontSize: '14px' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Violations"
                    value={complianceReport.violations}
                    valueStyle={{ color: '#ff4d4f', fontSize: '14px' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Total"
                    value={complianceReport.total_checks}
                    valueStyle={{ fontSize: '14px' }}
                  />
                </Col>
              </Row>
            </Card>

            {/* Cost Estimates */}
            {(complianceReport.estimated_permit_cost > 0 || complianceReport.estimated_compliance_cost > 0) && (
              <Card size="small" style={{ marginBottom: '16px' }}>
                <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                  <DollarOutlined /> Estimated Costs
                </Title>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text type="secondary">Permit Costs:</Text>
                    <br />
                    <Text strong>${complianceReport.estimated_permit_cost.toLocaleString()}</Text>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary">Compliance Costs:</Text>
                    <br />
                    <Text strong>${complianceReport.estimated_compliance_cost.toLocaleString()}</Text>
                  </Col>
                </Row>
                <Row style={{ marginTop: '8px' }}>
                  <Col span={24}>
                    <Text type="secondary">Total Estimated:</Text>
                    <br />
                    <Text strong style={{ fontSize: '16px' }}>
                      ${(complianceReport.estimated_permit_cost + complianceReport.estimated_compliance_cost).toLocaleString()}
                    </Text>
                  </Col>
                </Row>
              </Card>
            )}

            {/* Detailed Results */}
            <Title level={5}>Detailed Compliance Results</Title>
            <Collapse size="small" ghost>
              {Object.entries(groupResultsByType(complianceReport.results)).map(([type, results]) => (
                <Panel
                  header={
                    <Space>
                      <Badge count={results.filter(r => r.status === 'violation').length} size="small">
                        <Text strong>{type.replace('_', ' ').toUpperCase()}</Text>
                      </Badge>
                      <Text type="secondary">({results.length} checks)</Text>
                    </Space>
                  }
                  key={type}
                >
                  <List
                    dataSource={results}
                    renderItem={(result) => (
                      <List.Item
                        style={{
                          border: `1px solid ${getStatusColor(result.status)}`,
                          borderRadius: '4px',
                          marginBottom: '8px',
                          padding: '8px',
                          backgroundColor: result.status === 'violation' ? 'rgba(255, 77, 79, 0.05)' : 'transparent'
                        }}
                      >
                        <div style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <Space>
                              {getStatusIcon(result.status)}
                              <Text strong style={{ fontSize: '13px' }}>{result.title}</Text>
                            </Space>
                            <Space>
                              <Tag color={getSeverityColor(result.severity)} style={{ fontSize: '10px' }}>
                                {result.severity.toUpperCase()}
                              </Tag>
                              <Tag color={getStatusColor(result.status)} style={{ fontSize: '10px' }}>
                                {result.status.replace('_', ' ').toUpperCase()}
                              </Tag>
                            </Space>
                          </div>
                          
                          <Text style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
                            {result.description}
                          </Text>
                          
                          {result.recommendation && (
                            <Text style={{ fontSize: '11px', color: '#1890ff', marginBottom: '4px', display: 'block' }}>
                              💡 {result.recommendation}
                            </Text>
                          )}
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              {result.code_reference && (
                                <Text style={{ fontSize: '10px', color: '#999' }}>
                                  📋 {result.code_reference}
                                </Text>
                              )}
                            </div>
                            <div>
                              {result.estimated_cost && (
                                <Text style={{ fontSize: '11px', color: '#f50' }}>
                                  💰 ${result.estimated_cost.toLocaleString()}
                                </Text>
                              )}
                            </div>
                          </div>
                          
                          {result.required_permits && result.required_permits.length > 0 && (
                            <div style={{ marginTop: '4px' }}>
                              <Text style={{ fontSize: '10px', color: '#666' }}>Required Permits: </Text>
                              <Space wrap>
                                {result.required_permits.map((permit, i) => (
                                  <Tag key={i} color="blue">{permit}</Tag>
                                ))}
                              </Space>
                            </div>
                          )}
                        </div>
                      </List.Item>
                    )}
                  />
                </Panel>
              ))}
            </Collapse>
          </div>
        )}

        {/* Info Panel */}
        <div style={{ 
          background: '#f6f8fa', 
          padding: '12px', 
          borderRadius: '6px',
          border: '1px solid #e1e4e8'
        }}>
          <Text strong style={{ fontSize: '12px', color: '#586069' }}>
            🏛️ Compliance Checker Features
          </Text>
          <br />
          <Text style={{ fontSize: '11px', color: '#586069' }}>
            • Zoning compliance (setbacks, coverage, height limits)
            <br />
            • Building code validation (room sizes, ceiling heights)
            <br />
            • ADA accessibility requirements
            <br />
            • Fire safety and egress requirements
            <br />
            • Utility capacity and permit requirements
            <br />
            • Environmental impact assessment
            <br />
            • Cost estimates for permits and compliance
          </Text>
        </div>
      </Space>
    </Card>
  )
}

export default CompliancePanel
